import { useCallback, useEffect, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import './styles/app.css';
import { setAnalyticsUser, track } from './lib/analytics';
import { useSession } from './hooks/useSession';
import { useBrowseLocation } from './hooks/useBrowseLocation';
import { SheetsProvider, sheetSearchFromLegacy } from './hooks/useSheets';
import AuthModal from './components/AuthModal';
import Shell from './components/Shell';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import DetailPage from './pages/DetailPage';
import SellPage from './pages/SellPage';
import AccountPage from './pages/AccountPage';
import OnboardingPage from './pages/OnboardingPage';
import BlockedPage from './pages/BlockedPage';
import NotFoundPage from './pages/NotFoundPage';
import SplashScreen from './components/SplashScreen';

function RequireAuth({ user, onRequestAuth, children }) {
  useEffect(() => {
    if (!user) onRequestAuth?.();
  }, [user, onRequestAuth]);
  if (!user) return null;
  return children;
}

/** Legacy /messages, /notifications, /saved → current path (or /) with ?sheet params */
function SheetLegacyRedirect() {
  const location = useLocation();
  const search = sheetSearchFromLegacy(location.pathname);
  if (!search) return <Navigate to="/" replace />;
  return <Navigate to={{ pathname: '/', search }} replace />;
}

const SPLASH_MIN_MS = 2000;

/** Logs a page_view to Firebase Analytics on every route change. */
function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    track('page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);
  return null;
}

function AppRoutes() {
  const { user, profile, loading, needsOnboarding, isBlocked } = useSession();
  const browseLocation = useBrowseLocation();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const [splashMinElapsed, setSplashMinElapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const gated = !user;

  const requestAuth = useCallback((mode = 'signin') => {
    setAuthMode(mode);
    setAuthOpen(true);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setSplashMinElapsed(true), SPLASH_MIN_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (user) setAuthOpen(false);
    else setAuthOpen(true);
  }, [user]);

  useEffect(() => {
    setAnalyticsUser(user?.uid || null);
  }, [user?.uid]);

  useEffect(() => {
    if (location.pathname === '/auth') {
      requestAuth('signin');
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate, requestAuth]);

  if (loading || !splashMinElapsed) {
    return <SplashScreen />;
  }

  if (user && isBlocked) {
    return <BlockedPage profile={profile} />;
  }

  if (user && needsOnboarding) {
    return <OnboardingPage user={user} profile={profile} />;
  }

  return (
    <>
      <Shell
        user={user}
        browseLocation={browseLocation}
        onRequestAuth={() => requestAuth('signin')}
      >
        <Routes>
          <Route
            path="/"
            element={
              <HomePage
                user={user}
                browseLocation={browseLocation}
                onRequestAuth={() => requestAuth('signin')}
              />
            }
          />
          <Route
            path="/search"
            element={
              <RequireAuth user={user} onRequestAuth={() => requestAuth('signin')}>
                <SearchPage browseLocation={browseLocation} />
              </RequireAuth>
            }
          />
          <Route
            path="/listing/:id"
            element={
              <RequireAuth user={user} onRequestAuth={() => requestAuth('signin')}>
                <DetailPage
                  user={user}
                  browseLocation={browseLocation}
                  onRequestAuth={() => requestAuth('signin')}
                />
              </RequireAuth>
            }
          />
          <Route
            path="/sell"
            element={
              <RequireAuth user={user} onRequestAuth={() => requestAuth('signin')}>
                <SellPage user={user} />
              </RequireAuth>
            }
          />
          <Route
            path="/sell/new"
            element={
              <RequireAuth user={user} onRequestAuth={() => requestAuth('signin')}>
                <SellPage user={user} />
              </RequireAuth>
            }
          />
          <Route
            path="/sell/edit/:id"
            element={
              <RequireAuth user={user} onRequestAuth={() => requestAuth('signin')}>
                <SellPage user={user} />
              </RequireAuth>
            }
          />
          <Route path="/saved" element={<SheetLegacyRedirect />} />
          <Route path="/messages" element={<SheetLegacyRedirect />} />
          <Route path="/messages/:id" element={<SheetLegacyRedirect />} />
          <Route path="/notifications" element={<SheetLegacyRedirect />} />
          <Route
            path="/account"
            element={
              <RequireAuth user={user} onRequestAuth={() => requestAuth('signin')}>
                <AccountPage user={user} profile={profile} />
              </RequireAuth>
            }
          />
          <Route path="/auth" element={<Navigate to="/" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Shell>

      <AuthModal
        open={authOpen || gated}
        initialMode={authMode}
        dismissible={!gated}
        onClose={() => {
          if (!gated) setAuthOpen(false);
        }}
      />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <PageViewTracker />
      <SheetsProvider>
        <AppRoutes />
      </SheetsProvider>
    </BrowserRouter>
  );
}
